import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import {
  NgxDaterangepickerBootstrapComponent,
  NgxDaterangepickerBootstrapDirective,
  provideDaterangepickerLocale,
} from 'ngx-daterangepicker-bootstrap';

import * as Types from '../../../shared/types';
import dayjs, { Dayjs } from 'dayjs';
import * as Constants from '../../../shared/constant';
import { ButtonComponent } from '../../../components/button/button.component';
import { TruncatePipe } from '../../../pipes/truncate.pipe';
import { OutsideClickDirective } from '../../../directives/outside-click.directive';

interface NovaDateObject extends Object {
  startDate: dayjs.Dayjs | null;
  endDate: dayjs.Dayjs | null;
  label?: string | null;
}

@Component({
  selector: 'nova-daterange-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatInputModule,
    NgxDaterangepickerBootstrapDirective,
    NgxDaterangepickerBootstrapComponent,
    ButtonComponent,
    TruncatePipe,
    OutsideClickDirective,
  ],
  providers: [
    {
      provide: provideDaterangepickerLocale,
      useFactory: () =>
        provideDaterangepickerLocale({
          separator: ' - ',
          applyLabel: 'Okay',
        }),
    },
  ],
  templateUrl: './nova-daterange-picker.component.html',
  styleUrl: './nova-daterange-picker.component.scss',
})
export class NovaDaterangePickerComponent {
  @ViewChild(NgxDaterangepickerBootstrapDirective, { static: false }) pickerDirective1:
    | NgxDaterangepickerBootstrapDirective
    | undefined;
  @ViewChild('novaDatePickerInput', { read: ElementRef, static: false }) datePickerInputRef: ElementRef<HTMLInputElement> | undefined;
  @Input() singleDatePicker: boolean = false;
  @Input() showFilterUI: boolean = false;
  @Input() customFormat: string = 'YYYY-MM-DD';
  /** When in filter UI, empty value from parent (e.g. after operator change) resets the picker so user selects again. */
  @Input() valueFromParent: string | string[] | null = null;
  /** When true, dropdown opens upward (e.g. for NOVA input at bottom of screen). */
  @Input() openUpward: boolean = false;
  @Output() onDateSelect = new EventEmitter<string | string[]>();

  id = Math.random();

  divWidthTop: string = '70px'; // Default width
  divWidthBottom: string = '65px'; // Default width
  protected _dropdownOpened = signal<boolean>(false);

  // DateRange Picker Start
  dropsDown = 'down';
  dropsUp = 'up';
  opensRight = 'right';
  opensCenter = 'center';
  opensLeft = 'left';
  selectedRangeCalendarAutoLeft: Types.SelectedRangeCalendar = {} as Types.SelectedRangeCalendar;
  maxDate?: Dayjs;
  minDate?: Dayjs;
  invalidDates: Dayjs[] = [];
  startAPIDate: string | undefined;
  endAPIDate: string | undefined;
  selectedDate: string | undefined;
  referenceDate = dayjs().subtract(1, 'day');
  /** Preset key: empty start/end in API payload; shown as “Select Date Range” on the button. */
  private readonly unsetRangePresetKey = 'Select Date Range';
  ranges = {
    [this.unsetRangePresetKey]: [this.referenceDate.startOf('day'), this.referenceDate.startOf('day')],
    'Last 7 days': [this.referenceDate.subtract(6, 'days').startOf('day'), this.referenceDate.endOf('day')],
    'This month': [this.referenceDate.startOf('month'), this.referenceDate.endOf('month')],
    'Last 3 months': [
      this.referenceDate.subtract(3, 'months').startOf('month'),
      this.referenceDate.subtract(1, 'month').endOf('month'),
    ],
  } as any;
  localeTime = Constants.LocaleTime;
  locale = Constants.Locale;
  tooltips = [
    { date: dayjs(), text: '' },
    { date: dayjs().add(2, 'days'), text: '' },
  ];

  constructor(
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef
  ) {
    this.datepickerFunction();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['valueFromParent']) {
      const val = this.valueFromParent;
      const isEmpty = val === null || val === undefined || val === '' ||
        (Array.isArray(val) && val.length === 0);
      if (this.showFilterUI) {
        if (isEmpty) {
          this.resetFilterPickerSelection();
          this.datepickerFunction();
          return;
        }
        this.syncPickerFromParentValue(val);
        return;
      }
      // When not in filter UI (e.g. Nova), sync from parent when conversation changes
      if (!isEmpty && Array.isArray(val) && val.length >= 2) {
        this.syncPickerFromParentValue(val);
        return;
      }
    }
    this.datepickerFunction();
  }

  ngOnInit() {
    if (this.singleDatePicker || this.showFilterUI) {
      this.ranges = {};
    }
    // Keep a range already chosen by the parent (filter UI or a chart handoff into NOVA).
    const parentValue = this.valueFromParent;
    const parentHasRange = Array.isArray(parentValue)
      ? parentValue.some((value) => !!String(value || '').trim())
      : parentValue != null && parentValue !== '';
    if (parentHasRange) {
      this.syncPickerFromParentValue(parentValue as string | string[]);
    } else {
      this.datepickerFunction();
    }
  }

  ngOnDestroy() {
    this.pickerDirective1?.hide();
    this.cdr.detectChanges();
  }

  datepickerFunction() {
    if (this.singleDatePicker) {
      this.selectedRangeCalendarAutoLeft = {
        startDate: dayjs(),
        endDate: dayjs(),
      };
      this.divWidthBottom = '172px';
    }
    if (this.showFilterUI) {
      this.selectedRangeCalendarAutoLeft = {
        startDate: dayjs(),
        endDate: dayjs(),
      };
      this.divWidthBottom = '172px';
    } else {
      this.selectedRangeCalendarAutoLeft = {
        startDate: this.ranges[this.unsetRangePresetKey][0],
        endDate: this.ranges[this.unsetRangePresetKey][1],
        label: 'Select Date Range',
      };
    }
  }

  isInvalidDate = (m: Dayjs) => {
    return this.invalidDates.some((d) => d.isSame(m, 'day'));
  };

  isCustomDates = (date: Dayjs) => {
    return date.month() === 0 || date.month() === 6 ? 'mycustomdate' : false;
  };

  isTooltipDate = (m: Dayjs) => {
    const tooltip = this.tooltips.find((tt) => tt.date.isSame(m, 'day'));
    return tooltip ? tooltip.text : false;
  };

  toTitleCase = (str: string) => {
    return str.replace(/\w\S*/g, (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase());
  };

  /** True when the “Select Date Range” preset is active (empty API dates). */
  private isUnsetRangePresetLabel(label: string | undefined | null): boolean {
    return (label ?? '').trim().toLowerCase() === 'select date range';
  }

  datesUpdatedRange($event: object) {
    const updatedRangeObject = $event as NovaDateObject;

    if (this.singleDatePicker) {
      this.selectedDate = updatedRangeObject?.startDate?.format(this.customFormat);
      const apiDate = updatedRangeObject?.startDate?.format('YYYY-MM-DD');
      if (this.onDateSelect) {
        this.onDateSelect.emit(apiDate);
      }
    } else if (updatedRangeObject?.label) {
      this.selectedRangeCalendarAutoLeft.label = this.toTitleCase(updatedRangeObject.label);

      if (this.selectedRangeCalendarAutoLeft.label === 'Custom Range') {
        this.selectedRangeCalendarAutoLeft.label = 'Custom range';
      }

      const unsetRange = this.isUnsetRangePresetLabel(this.selectedRangeCalendarAutoLeft.label);
      if (unsetRange) {
        this.divWidthTop = '160px';
        this.divWidthBottom = '154px';
      } else if (this.selectedRangeCalendarAutoLeft.label.length < 12) {
        this.divWidthTop = '120px';
        this.divWidthBottom = '114px';
      } else if (this.selectedRangeCalendarAutoLeft.label.length < 15) {
        this.divWidthTop = '135px';
        this.divWidthBottom = '129px';
      } else {
        this.divWidthTop = '200px';
        this.divWidthBottom = '195px';
      }
      if (unsetRange) {
        this.startAPIDate = '';
        this.endAPIDate = '';
      } else {
        this.startAPIDate = this.selectedRangeCalendarAutoLeft.startDate.format('YYYY-MM-DD');
        this.endAPIDate = this.selectedRangeCalendarAutoLeft.endDate.format('YYYY-MM-DD');
      }

      // Emit range for parents (e.g. NOVA) that need start/end without filter UI
      if (this.onDateSelect) {
        this.onDateSelect.emit(unsetRange ? ['', ''] : [this.startAPIDate ?? '', this.endAPIDate ?? '']);
      }
    } else {
      // filter date range picker logic
      if (this.showFilterUI) {
        this.selectedDate = `${updatedRangeObject?.startDate?.format(this.customFormat)} - ${updatedRangeObject?.endDate?.format(this.customFormat)}`;
        const apiDate: [string, string] = [
          updatedRangeObject?.startDate?.format('YYYY-MM-DD') ?? '',
          updatedRangeObject?.endDate?.format('YYYY-MM-DD') ?? '',
        ];
        if (this.onDateSelect) {
          this.onDateSelect.emit(apiDate);
        }
      }
    }
  }

  //load plots functions
  loadFetchFunctions() {
    this.cdr.detectChanges();
  }

  openDatepicker() {}

  toggleDropdown = () => {
    const picker = this.pickerDirective1;
    const inputEl = this.datePickerInputRef?.nativeElement;

    const openPicker = () => {
      if (picker) {
        picker.toggle();
      } else if (inputEl && this.openUpward) {
        // Fallback when ViewChild not ready (e.g. NOVA): trigger directive's host (click) -> open()
        inputEl.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      }
      this.cdr.detectChanges();
    };

    // Defer so the directive and input are ready (fixes NOVA where picker was not opening)
    setTimeout(openPicker, 0);
  };

  handleOutSideClick = () => {
    this.pickerDirective1?.hide();
    this.cdr.detectChanges();
  };

  handleButtonClick = (event: MouseEvent) => {
    this.toggleDropdown();
    event?.stopPropagation();
  };

  /** When openUpward (NOVA), click on trigger row (but not on the button or input) opens the picker. */
  onTriggerClick = (event: MouseEvent) => {
    if (!this.openUpward) return;
    const target = event.target as Node;
    const inputEl = this.datePickerInputRef?.nativeElement;
    // Avoid double-toggle: ignore clicks on the input or on the button (button-wrapper)
    if (inputEl && (event.target === inputEl || inputEl.contains(target))) return;
    const host = this.elementRef.nativeElement as HTMLElement;
    const buttonWrapper = host?.querySelector?.('.button-wrapper');
    if (buttonWrapper?.contains(target)) return;
    this.toggleDropdown();
    event?.stopPropagation();
  };

  getDateLabel = () => {
    return this.selectedRangeCalendarAutoLeft.label ?? '';
  };

  /** Clears the selected date in filter UI when parent value is cleared (e.g. operator changed). */
  private resetFilterPickerSelection(): void {
    if (!this.showFilterUI) return;
    this.selectedDate = undefined;
    this.pickerDirective1?.clear();
    this.cdr.detectChanges();
  }

  /** Syncs picker model from parent value so the displayed selection is not overwritten by datepickerFunction (today). */
  private syncPickerFromParentValue(val: string | string[]): void {
    if (Array.isArray(val)) {
      if (!val[0] && !val[1]) {
        this.selectedRangeCalendarAutoLeft = {
          startDate: this.ranges[this.unsetRangePresetKey][0],
          endDate: this.ranges[this.unsetRangePresetKey][1],
          label: 'Select Date Range',
        };
        this.selectedDate = undefined;
        this.cdr.detectChanges();
        return;
      }
      const start = val[0] ? dayjs(val[0]) : dayjs();
      const end = val[1] ? dayjs(val[1]) : start;
      const label = this.getLabelForRange(start, end);
      this.selectedRangeCalendarAutoLeft = { startDate: start, endDate: end, label };
      this.selectedDate = `${start.format(this.customFormat)} - ${end.format(this.customFormat)}`;
    } else {
      const d = val ? dayjs(val) : dayjs();
      this.selectedRangeCalendarAutoLeft = { startDate: d, endDate: d };
      this.selectedDate = d.format(this.customFormat);
    }
    this.cdr.detectChanges();
  }

  /** Resolve label for a range (preset name or "Custom range") when syncing from parent (e.g. Nova conversation context). */
  private getLabelForRange(start: Dayjs, end: Dayjs): string {
    if (!this.ranges || typeof this.ranges !== 'object') return 'Custom range';
    for (const [name, range] of Object.entries(this.ranges)) {
      if (name === this.unsetRangePresetKey) continue;
      if (Array.isArray(range) && range.length >= 2) {
        const rStart = dayjs(range[0]);
        const rEnd = dayjs(range[1]);
        if (start.isSame(rStart, 'day') && end.isSame(rEnd, 'day')) {
          return this.toTitleCase(name);
        }
      }
    }
    return 'Custom range';
  }
}
